import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { hiresmartDb } from '../firebase';
import './Careers.css';
import '../pages/Services/Services.css';

const jobOpenings = [
  {
    id: 1,
    title: 'Full Stack Developer',
    location: 'McKinney, TX',
    type: 'Full-time',
    experience: '3-5 years',
    description:
      'We are looking for a Full Stack Developer experienced in React and Node.js to join our dynamic development team. The ideal candidate will be responsible for developing and maintaining web applications, ensuring high performance and responsiveness.',
    requirements: [
      'Strong proficiency in JavaScript, including DOM manipulation and the JavaScript object model',
      'Experience with React.js and its core principles',
      'Experience with popular React.js workflows (Redux)',
      'Experience with Node.js',
      'Familiarity with RESTful APIs',
      'Knowledge of modern authorization mechanisms',
      'Familiarity with modern front-end build pipelines and tools',
    ],
  },
  {
    id: 2,
    title: 'Automation Developer',
    location: 'McKinney, TX',
    type: 'Full-time',
    experience: '2-4 years',
    description:
      'We are seeking an experienced Automation Developer to design and implement automated testing solutions. The ideal candidate will create robust test frameworks and help improve our continuous integration processes.',
    requirements: [
      'Strong experience with test automation frameworks (Selenium, Cypress, or similar)',
      'Proficiency in Python, Java, or JavaScript for test automation',
      'Experience with API testing and tools like Postman, REST Assured',
      'Knowledge of CI/CD pipelines and tools (Jenkins, GitLab CI)',
      'Understanding of Agile methodologies and testing practices',
      'Experience with version control systems (Git)',
      'Strong analytical and problem-solving skills',
    ],
  },
  {
    id: 3,
    title: 'DevOps Engineer',
    location: 'McKinney, TX',
    type: 'Full-time',
    experience: '3-5 years',
    description:
      'We are looking for a DevOps Engineer to help build and maintain our cloud infrastructure and deployment pipelines. The ideal candidate will work closely with development teams to implement efficient CI/CD processes and ensure system reliability.',
    requirements: [
      'Strong experience with cloud platforms (AWS, Azure, or GCP)',
      'Expertise in containerization (Docker) and orchestration (Kubernetes)',
      'Experience with Infrastructure as Code (Terraform, CloudFormation)',
      'Proficiency in scripting languages (Python, Bash, PowerShell)',
      'Experience with CI/CD tools (Jenkins, GitLab CI, GitHub Actions)',
      'Knowledge of monitoring and logging solutions',
      'Understanding of security best practices and compliance requirements',
      'Experience with version control systems and branching strategies',
    ],
  },
  {
    id: 4,
    title: 'Data Engineer',
    location: 'McKinney, TX',
    type: 'Full-time',
    experience: '3-5 years',
    description:
      'We are seeking a skilled Data Engineer to design and implement data pipelines, warehouses, and ETL processes. The ideal candidate will work with cross-functional teams to transform raw data into valuable business insights while ensuring data quality and accessibility.',
    requirements: [
      'Strong experience with SQL and database management systems (PostgreSQL, MySQL, MongoDB)',
      'Proficiency in Python or Java for data processing and analysis',
      'Experience with ETL tools and data warehousing solutions',
      'Knowledge of big data technologies (Hadoop, Spark, or similar)',
      'Experience with cloud-based data services (AWS Redshift, Google BigQuery, or Azure Synapse)',
      'Understanding of data modeling and dimensional modeling concepts',
      'Experience with data visualization tools (Tableau, Power BI)',
      'Knowledge of data governance and security best practices',
    ],
  },
];

const Careers = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const querySnapshot = await getDocs(collection(hiresmartDb, 'jobs'));
        const list = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (!data.closed && data.status !== 'Closed') {
            list.push({ id: doc.id, ...data });
          }
        });
        // Sort by ID descending (newest jobs first)
        list.sort((a, b) => b.id.localeCompare(a.id));
        
        // In local development, if Firestore returns no jobs (due to dummy keys/offline), fallback to static jobs
        if (list.length === 0 && import.meta.env.DEV) {
          setJobs(jobOpenings.slice(0, 4));
        } else {
          setJobs(list.slice(0, 4));
        }
      } catch (error) {
        console.error("Error fetching jobs from HireSmart portal:", error);
        setJobs(jobOpenings.slice(0, 4));
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const handleApply = (jobId) => {
    window.open(
      `https://hiresmart.sradhatech.com/candidates/jobs/${jobId}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div className="careers-page">
      <div className="service-header">
        <h1>Join Our Team</h1>
        <p>Be part of our innovative and dynamic team</p>
      </div>

      <div className="container">
        <div className="careers-content">
          <div className="job-listings">
            <h2>Open Positions</h2>
            {loading ? (
              <div className="jobs-loading">
                <div className="spinner"></div>
                <p>Loading open positions...</p>
              </div>
            ) : jobs.length === 0 ? (
              <div className="no-jobs">
                <i className="fas fa-briefcase" aria-hidden="true" />
                <p>No open positions at the moment. Please check back later!</p>
              </div>
            ) : (
              jobs.map((job) => (
                <div
                  key={job.id}
                  className={`job-card ${selectedJob === job.id ? 'active' : ''}`}
                  onClick={() => setSelectedJob(job.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setSelectedJob(job.id);
                    }
                  }}
                >
                  <div className="job-header">
                    <h3>{job.title}</h3>
                    <div className="job-meta">
                      <span>
                        <i className="fas fa-map-marker-alt" aria-hidden="true" />{' '}
                        {job.location}
                      </span>
                      <span>
                        <i className="fas fa-briefcase" aria-hidden="true" />{' '}
                        {job.type}
                      </span>
                      <span>
                        <i className="fas fa-clock" aria-hidden="true" />{' '}
                        {job.experience}
                      </span>
                    </div>
                  </div>

                  <div
                    className={`job-details ${
                      selectedJob === job.id ? 'show' : ''
                    }`}
                  >
                    <p>{job.description}</p>
                    {Array.isArray(job.requirements) && job.requirements.length > 0 && (
                      <div className="requirements">
                        <h4>Requirements:</h4>
                        <ul>
                          {job.requirements.map((req, index) => (
                            <li key={index}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <button
                      type="button"
                      className="apply-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApply(job.id);
                      }}
                      aria-label={`Apply for ${job.title} position`}
                    >
                      <span className="button-text">Apply Now</span>
                      <span className="button-icon">
                        <i className="fas fa-arrow-right" aria-hidden="true" />
                      </span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="careers-info">
            <div className="info-card">
              <h3>Why Choose Us?</h3>
              <ul>
                <li>Innovation-driven culture</li>
                <li>Continuous learning &amp; growth</li>
                <li>Work on cutting-edge tech</li>
                <li>Global impact opportunities</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Careers;
